import { Card, Group, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import type { DashboardRow } from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';
import { StatusBadge } from './StatusBadge.tsx';
import { LifecycleActions } from './LifecycleActions.tsx';

interface Props {
  readonly reservation: DashboardRow;
  readonly timezone: string;
  readonly customerLabel: string;
  readonly emoji: string;
  readonly onAllocate: () => void;
  readonly onCheckIn: () => void;
  readonly onComplete: () => void;
  readonly onCancel: () => void;
  readonly onNoShow: () => void;
  readonly busy?: boolean;
}

function fmtTimeRange(startIso: string, endIso: string, tz: string): string {
  const s = dayjs(startIso).tz(tz).format('HH:mm');
  const e = dayjs(endIso).tz(tz).format('HH:mm');
  return `${s}  →  ${e}`;
}

function durationMin(startIso: string, endIso: string): number {
  return Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60000);
}

export function ReservationCard(props: Props) {
  const {
    reservation,
    timezone,
    customerLabel,
    emoji,
    onAllocate,
    onCheckIn,
    onComplete,
    onCancel,
    onNoShow,
    busy,
  } = props;
  const accent = tokens.status[reservation.status].accent;
  return (
    <Card
      radius="xl"
      padding="lg"
      shadow="xs"
      withBorder
      style={{
        borderLeft: `6px solid ${accent}`,
        background: tokens.color.cardBackground,
      }}
    >
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={700} style={{ fontSize: 22, letterSpacing: 0.3, color: tokens.color.primaryInk }}>
              {fmtTimeRange(reservation.startTime, reservation.endTime, timezone)}
            </Text>
            <Text size="xs" c="dimmed">
              時長 {durationMin(reservation.startTime, reservation.endTime)} 分鐘
            </Text>
          </Stack>
          <StatusBadge status={reservation.status} />
        </Group>
        <Group gap="xs" mt={4}>
          <Text style={{ fontSize: 20 }}>{emoji}</Text>
          <Text fw={600} size="md" style={{ color: tokens.color.primaryInk }}>
            {customerLabel}
          </Text>
          <Text size="sm" c="dimmed">
            · {reservation.partySize} 位客人
          </Text>
        </Group>
        <LifecycleActions
          reservation={reservation}
          onAllocate={onAllocate}
          onCheckIn={onCheckIn}
          onComplete={onComplete}
          onCancel={onCancel}
          onNoShow={onNoShow}
          busy={busy === true}
        />
      </Stack>
    </Card>
  );
}
